import { Button } from '@/components/ui/button';
import {
  ZoomIn,
  ZoomOut,
  Layout,
  Trash2,
  Plus,
  ArrowRight,
  ArrowDown,
  MonitorSmartphone,
  Undo2,
  Redo2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useWorkflowStore, type MapFlowOrientation } from '@/stores/workflowStore';
import { BlockPaletteMenu } from '@/components/BlockPaletteMenu';
import type { BlockTemplate } from '@/lib/blockPalette';
import type { Workflow } from '@/stores/workflowStore';
import type { ResolvedMapFlowOrientation } from '@/lib/mapFlowOrientation';
import { inferLayoutOrientation } from '@/lib/mapFlowOrientation';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface CanvasToolbarProps {
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  zoom?: number;
  workflow?: Workflow;
  onAddBlock?: (template: BlockTemplate) => void;
  flowOrientation?: MapFlowOrientation;
  effectiveFlowOrientation?: ResolvedMapFlowOrientation;
  onFlowOrientationChange?: (orientation: MapFlowOrientation) => void;
  /** floating = on-canvas overlay; strip = mission sub-nav row */
  variant?: 'floating' | 'strip';
  /** Light controls on inverted mission header band */
  inverted?: boolean;
  className?: string;
}

export function CanvasToolbar({
  onZoomIn,
  onZoomOut,
  zoom = 100,
  workflow,
  onAddBlock,
  flowOrientation = 'auto',
  effectiveFlowOrientation = 'horizontal',
  onFlowOrientationChange,
  variant = 'floating',
  inverted = false,
  className,
}: CanvasToolbarProps) {
  const undoStackSize = useWorkflowStore((s) => s.mapUndoPast.length);
  const redoStackSize = useWorkflowStore((s) => s.mapUndoFuture.length);
  const undoWorkflowId = useWorkflowStore((s) => s.mapUndoWorkflowId);
  const {
    currentWorkflow,
    selectNode,
    applyFlowLayout,
    updateMissionMeta,
    saveWorkflow,
    undoMap,
    redoMap,
    recordMapUndoPoint,
  } = useWorkflowStore();
  const activeWorkflow = workflow ?? currentWorkflow;
  const undoEnabled =
    Boolean(currentWorkflow) &&
    undoWorkflowId === currentWorkflow?.id &&
    undoStackSize > 0;
  const redoEnabled =
    Boolean(currentWorkflow) &&
    undoWorkflowId === currentWorkflow?.id &&
    redoStackSize > 0;

  const handleAutoLayout = () => {
    if (!currentWorkflow || currentWorkflow.nodes.length === 0) {
      toast.info('Add blocks first');
      return;
    }

    applyFlowLayout(effectiveFlowOrientation, { mode: 'tidy' });
    selectNode(null);
    toast.success('Map tidied — block order preserved (Undo available)');
  };

  const handleFlowOrientation = (next: MapFlowOrientation) => {
    const prevResolved =
      flowOrientation === 'vertical'
        ? 'vertical'
        : flowOrientation === 'horizontal'
          ? 'horizontal'
          : effectiveFlowOrientation;

    updateMissionMeta({ flowOrientation: next });
    saveWorkflow();

    if (currentWorkflow && currentWorkflow.nodes.length > 0) {
      const target =
        next === 'auto' ? effectiveFlowOrientation : (next as ResolvedMapFlowOrientation);
      const from =
        prevResolved === target
          ? inferLayoutOrientation(currentWorkflow.nodes)
          : prevResolved;

      applyFlowLayout(target, {
        mode: 'reorient',
        fromOrientation: from,
      });
    }

    onFlowOrientationChange?.(next);
    const label =
      next === 'auto'
        ? `Auto flow (${effectiveFlowOrientation})`
        : next === 'vertical'
          ? 'Vertical flow'
          : 'Horizontal flow';
    toast.message(`${label} — map reoriented, not reset`);
  };

  const handleClearCanvas = () => {
    if (!currentWorkflow || currentWorkflow.nodes.length === 0) {
      toast.info('Canvas is empty');
      return;
    }

    if (confirm('Clear all blocks? You can undo this from the map toolbar.')) {
      recordMapUndoPoint();
      useWorkflowStore.setState((state) => {
        if (!state.currentWorkflow) return state;
        return {
          currentWorkflow: {
            ...state.currentWorkflow,
            nodes: [],
            edges: [],
          },
          connectFromNodeId: null,
        };
      });
      selectNode(null);
      toast.success('Canvas cleared');
    }
  };

  const FlowIcon =
    flowOrientation === 'vertical'
      ? ArrowDown
      : flowOrientation === 'horizontal'
        ? ArrowRight
        : MonitorSmartphone;

  const handleUndo = () => {
    if (undoMap()) toast.message('Undid last map change');
    else toast.info('Nothing to undo');
  };

  const handleRedo = () => {
    if (redoMap()) toast.message('Redid map change');
    else toast.info('Nothing to redo');
  };

  const toolBtn =
    variant === 'strip'
      ? 'h-8 px-2.5 rounded-md text-xs font-medium gap-1.5'
      : 'h-8 w-8 p-0';

  const toolbarInner = (
    <>
      <div className={cn('flex flex-wrap items-center gap-1', variant === 'strip' ? 'gap-1.5' : 'gap-2')}>
        {activeWorkflow && onAddBlock && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                actionTier={variant === 'strip' ? 'secondary' : 'primary'}
                className={cn(variant === 'floating' && 'ui-lift shadow-lg', variant === 'strip' && toolBtn)}
                title="Add a block to the map"
              >
                <Plus className="w-4 h-4 shrink-0" />
                {variant === 'strip' ? <span>Add block</span> : <span className="sr-only sm:not-sr-only sm:ml-1.5">Add block</span>}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-56 max-h-[min(70vh,480px)] overflow-y-auto p-1"
            >
              <BlockPaletteMenu workflow={activeWorkflow} onPick={onAddBlock} />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      <div
        className={cn(
          'flex flex-wrap items-center gap-1',
          variant === 'floating' &&
            'gap-2 bg-card border border-border rounded-lg shadow-lg p-2 ml-auto',
          variant === 'strip' && 'gap-1.5 ml-auto'
        )}
      >
        <Button variant="ghost" size="sm" className={toolBtn} title="Zoom in" onClick={onZoomIn}>
          <ZoomIn className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden sm:inline">Zoom in</span> : null}
        </Button>
        <div
          className={cn(
            'text-xs font-medium text-muted-foreground tabular-nums',
            variant === 'strip' ? 'px-1' : 'px-2 py-1'
          )}
        >
          {zoom}%
        </div>
        <Button variant="ghost" size="sm" className={toolBtn} title="Zoom out" onClick={onZoomOut}>
          <ZoomOut className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden sm:inline">Zoom out</span> : null}
        </Button>
        <div className="w-px bg-border h-6 hidden sm:block" />
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Undo (⌘Z)"
          onClick={handleUndo}
          disabled={!undoEnabled}
          aria-label="Undo map change"
        >
          <Undo2 className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden md:inline">Undo</span> : null}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Redo (⇧⌘Z)"
          onClick={handleRedo}
          disabled={!redoEnabled}
          aria-label="Redo map change"
        >
          <Redo2 className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden md:inline">Redo</span> : null}
        </Button>
        <div className="w-px bg-border h-6 hidden sm:block" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={toolBtn}
              title="Flow orientation"
            >
              <FlowIcon className="w-4 h-4" />
              {variant === 'strip' ? <span className="hidden md:inline">Orient</span> : null}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Flow orientation</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('vertical')}
              className={cn(flowOrientation === 'vertical' && 'bg-accent')}
            >
              <ArrowDown className="w-4 h-4 mr-2" />
              Vertical (top → bottom)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('horizontal')}
              className={cn(flowOrientation === 'horizontal' && 'bg-accent')}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Horizontal (left → right)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('auto')}
              className={cn(flowOrientation === 'auto' && 'bg-accent')}
            >
              <MonitorSmartphone className="w-4 h-4 mr-2" />
              Auto — now {effectiveFlowOrientation}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Tidy spacing along flow axis"
          onClick={handleAutoLayout}
        >
          <Layout className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden md:inline">Tidy</span> : null}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(toolBtn, 'text-destructive hover:text-destructive')}
          title="Clear canvas"
          onClick={handleClearCanvas}
        >
          <Trash2 className="w-4 h-4" />
          {variant === 'strip' ? <span className="hidden md:inline">Clear</span> : null}
        </Button>
      </div>
    </>
  );

  if (variant === 'strip') {
    return (
      <div
        className={cn(
          'flex flex-wrap items-center gap-2 overflow-x-auto pb-0.5 -mx-1 px-1',
          className
        )}
        role="toolbar"
        aria-label="Map tools"
        {...(inverted ? { 'data-mission-tool': true } : {})}
      >
        {toolbarInner}
      </div>
    );
  }

  return (
    <div className="absolute top-4 left-4 right-4 flex flex-wrap items-start justify-between gap-2 pointer-events-none z-10">
      <div className="pointer-events-auto flex gap-2">
        {activeWorkflow && onAddBlock && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="ui-lift shadow-lg" title="Add a block to the map">
                <Plus className="w-4 h-4 mr-1.5" />
                Add block
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className="w-56 max-h-[min(70vh,480px)] overflow-y-auto p-1"
            >
              <BlockPaletteMenu workflow={activeWorkflow} onPick={onAddBlock} />
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div
        className={cn(
          'pointer-events-auto flex flex-wrap items-center gap-2 bg-card border border-border rounded-lg shadow-lg p-2 ml-auto'
        )}
      >
        <Button variant="ghost" size="sm" className={toolBtn} title="Zoom in" onClick={onZoomIn}>
          <ZoomIn className="w-4 h-4" />
        </Button>
        <div className="px-2 py-1 text-xs font-medium text-muted-foreground tabular-nums">{zoom}%</div>
        <Button variant="ghost" size="sm" className={toolBtn} title="Zoom out" onClick={onZoomOut}>
          <ZoomOut className="w-4 h-4" />
        </Button>
        <div className="w-px bg-border h-6" />
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Undo (⌘Z)"
          onClick={handleUndo}
          disabled={!undoEnabled}
          aria-label="Undo map change"
        >
          <Undo2 className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Redo (⇧⌘Z)"
          onClick={handleRedo}
          disabled={!redoEnabled}
          aria-label="Redo map change"
        >
          <Redo2 className="w-4 h-4" />
        </Button>
        <div className="w-px bg-border h-6" />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              title="Flow orientation"
              className={cn(toolBtn, flowOrientation === 'auto' && 'text-ab-green-core dark:text-ab-green-glow')}
            >
              <FlowIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Flow orientation</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('vertical')}
              className={cn(flowOrientation === 'vertical' && 'bg-accent')}
            >
              <ArrowDown className="w-4 h-4 mr-2" />
              Vertical (top → bottom)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('horizontal')}
              className={cn(flowOrientation === 'horizontal' && 'bg-accent')}
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Horizontal (left → right)
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => handleFlowOrientation('auto')}
              className={cn(flowOrientation === 'auto' && 'bg-accent')}
            >
              <MonitorSmartphone className="w-4 h-4 mr-2" />
              Auto — now {effectiveFlowOrientation}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <Button
          variant="ghost"
          size="sm"
          className={toolBtn}
          title="Tidy spacing along flow axis"
          onClick={handleAutoLayout}
        >
          <Layout className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={cn(toolBtn, 'text-destructive hover:text-destructive')}
          title="Clear canvas"
          onClick={handleClearCanvas}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
